import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { seriesPath } from 'src/app/utils/series-url';

export interface YearRailItem {
  /** Series identifier as used in the URL, e.g. '27', '76', '02'. */
  year: string;
  /** Calendar label under the plate, e.g. 'Sep 2025'. */
  label: string;
  /** Short green tag for a series not yet on sale (27). */
  tag?: string;
  /** Thin divider before this item: first series of an earlier calendar year. */
  yearBreak: boolean;
}

/** Every current-style series with a page, newest first: 27 (pre-release),
 *  then September (76) before March (26) within each year, back to 51
 *  (September 2001, the first current-style series). */
export function buildYearRail(preReleaseTag: Record<string, string> = { '27': 'Live' }): YearRailItem[] {
  const out: YearRailItem[] = [];
  const push = (year: string, cal: number, month: string) => {
    const prev = out[out.length - 1];
    out.push({
      year,
      label: `${month} ${cal}`,
      tag: preReleaseTag[year],
      yearBreak: !!prev && !prev.label.endsWith(String(cal)),
    });
  };
  push('27', 2027, 'Mar');
  for (let y = 26; y >= 2; y--) {
    push(String(y + 50), 2000 + y, 'Sep');
    push(String(y).padStart(2, '0'), 2000 + y, 'Mar');
  }
  push('51', 2001, 'Sep');
  return out;
}

/**
 * Series switcher: a strip of mini number plates, one per series, newest
 * first. Other series are white front plates, faded; the series on screen
 * is the yellow rear plate, scrolled to the centre of the strip. Phones
 * swipe the strip; wider screens also get arrow buttons.
 */
@Component({
  selector: 'pla-new-release-year-rail',
  templateUrl: './new-release-year-rail.component.html',
  styleUrls: ['./new-release-year-rail.component.scss'],
})
export class NewReleaseYearRailComponent implements OnChanges, AfterViewInit {
  /** Current series, e.g. '75'. */
  @Input() year = '';
  /** Route prefix the links stay on: '/new-release' or '/new-release-draft'. */
  /** Route prefix, or 'series' for the canonical /plates/{NN}-reg pages. */
  @Input() basePath = 'series';

  link(year: string): string[] {
    return this.basePath === 'series' ? [seriesPath(year)] : [this.basePath, year];
  }

  @ViewChild('scroller') scroller?: ElementRef<HTMLElement>;

  readonly items = buildYearRail();
  canLeft = false;
  canRight = true;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['year'] && !changes['year'].firstChange) {
      setTimeout(() => this.centreCurrent(false));
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.centreCurrent(false));
  }

  trackByYear(_: number, item: YearRailItem): string { return item.year; }

  /** Scroll the strip (never the page) so the current plate sits centred. */
  centreCurrent(smooth: boolean): void {
    const sc = this.scroller?.nativeElement;
    const cur = sc?.querySelector('.is-current') as HTMLElement | null;
    if (!sc || !cur) return;
    const left = cur.offsetLeft - (sc.clientWidth - cur.offsetWidth) / 2;
    sc.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
    this.updateEdges();
  }

  page(dir: 1 | -1): void {
    const sc = this.scroller?.nativeElement;
    if (!sc) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    sc.scrollBy({ left: dir * sc.clientWidth * 0.7, behavior: reduced ? 'auto' : 'smooth' });
  }

  updateEdges(): void {
    const sc = this.scroller?.nativeElement;
    if (!sc) return;
    this.canLeft = sc.scrollLeft > 4;
    this.canRight = sc.scrollLeft + sc.clientWidth < sc.scrollWidth - 4;
  }
}
